import { useState } from 'react';
import {
  FACET_LABELS,
  RESEARCH_PASS_LABELS,
  factLine,
  type Fact,
  type RunFindings,
} from '@ni/shared';
import { useRunFindings } from '../../api/hooks';
import { RUN_COVERAGE_LABELS, runFindingsLine } from '../../lib/copy';
import { formatDay } from '../../lib/format';
import { Modal } from '../ui/modal';
import { Button } from '../ui/button';

/**
 * **What each later run found** (dossier Re-run and Refresh spec §3.5): the
 * Re-runs and Refreshes after the first dossier, each with its date and one line,
 * and opening one shows its facts grouped and its coverage. The dossier itself is
 * not versioned; this is the account of what each run added, including a Refresh
 * that found nothing. Renders nothing until there is a later run.
 */
export function LaterRuns({
  subjectId,
  subjectName,
  facts,
}: {
  subjectId: string;
  subjectName: string;
  facts: readonly Fact[];
}) {
  const findings = useRunFindings(subjectId);
  const [openId, setOpenId] = useState<string | null>(null);
  const entries = findings.data?.entries ?? [];
  if (entries.length === 0) return null;
  const opened = entries.find((entry) => entry.run_id === openId) ?? null;

  return (
    <div className="section later-runs">
      <h2>
        <span className="cat-head static">Later runs</span>
      </h2>
      {entries.map((entry) => (
        <div className="act-row" key={entry.run_id}>
          <div className="act-name">
            <button type="button" className="linkish flush" onClick={() => setOpenId(entry.run_id)}>
              {RESEARCH_PASS_LABELS[entry.research_pass]}
            </button>
          </div>
          <div className="act-state">{runFindingsLine(entry)}</div>
          <div className="act-value">{formatDay(entry.completed_at)}</div>
        </div>
      ))}

      {opened ? (
        <Modal
          label={RESEARCH_PASS_LABELS[opened.research_pass]}
          wide
          onClose={() => setOpenId(null)}
        >
          <div className="modal-head">
            {RESEARCH_PASS_LABELS[opened.research_pass]} · {formatDay(opened.completed_at)}
          </div>
          <div className="modal-body">
            <RunFindingsView
              entry={opened}
              subjectId={subjectId}
              subjectName={subjectName}
              facts={facts}
            />
          </div>
          <div className="modal-actions">
            <Button variant="act" onClick={() => setOpenId(null)}>
              Close
            </Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/** One later run's findings: new, confirmed and changed facts, then its coverage. */
export function RunFindingsView({
  entry,
  subjectId,
  subjectName,
  facts,
}: {
  entry: RunFindings;
  subjectId: string;
  subjectName: string;
  facts: readonly Fact[];
}) {
  const byLineage = new Map(facts.map((fact) => [fact.lineage_id, fact]));
  const group = (title: string, lineages: readonly string[]) => {
    const rows = lineages.flatMap((lineage) => byLineage.get(lineage) ?? []);
    if (rows.length === 0) return null;
    return (
      <div className="cat-findings">
        <div className="cat-head static">{title}</div>
        {rows.map((fact) => (
          <div className="cat-item" key={fact.lineage_id}>
            <div className="cat-line">{factLine(fact, { id: subjectId, name: subjectName })}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="modal-line">{runFindingsLine(entry)}.</div>
      {group('New', entry.new)}
      {group('Changed', entry.changed)}
      {group('Confirmed', entry.confirmed)}
      {entry.coverage.length > 0 ? (
        <div className="cat-findings">
          <div className="cat-head static">Coverage</div>
          {entry.coverage.map((row) => (
            <div className="act-row" key={row.facet}>
              <div className="act-name">
                {(FACET_LABELS as Record<string, string | undefined>)[row.facet] ?? row.facet}
              </div>
              <div className="act-state">{RUN_COVERAGE_LABELS[row.status]}</div>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}
